import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { message, Upload, type UploadFile } from 'antd';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import styles from './AvatarUpload.module.css';
import { EventInfoFormStateType } from '../../../EventInfoForm/types';
import {
  MEDIA_ACCEPT_EXT,
  DEFAULT_IMAGE_MAX_MB,
  DEFAULT_VIDEO_MAX_MB,
  isAcceptedMediaMime,
  validateMediaSizeMB,
  isVideoMime,
  mediaAllowedTypesMessage,
  isImageMime,
} from 'lib.shared/src/media';
import { apiBaseUrl } from 'lib.shared/src/api';
import LogoCropModal, { CroppedAreaPixels } from './LogoCropModal';

type AvatarUploadPropsType = {
  url?: string;
  onChangeFormState: (fieldName: keyof EventInfoFormStateType, value: string) => void;
  onChangeLogoSquare?: (url: string) => void;
  /** backend storage folder for the uploaded/cropped files */
  folder?: string;
  /**
   * The frame the logo shows in and is cropped for. Both store the same
   * square crop (`logo_square`) — the shape is only how it reads on screen.
   */
  shape?: 'circle' | 'square';
};

/** Opens the file picker from outside — a "Replace it" button next to the preview. */
export interface AvatarUploadHandle {
  open: () => void;
}

const imageUploadUrl = apiBaseUrl + '/partner/landings/image';
const imageCropUrl = apiBaseUrl + '/partner/landings/image/crop';

const AvatarUpload = forwardRef<AvatarUploadHandle, AvatarUploadPropsType>((props, ref) => {
  const { onChangeFormState, onChangeLogoSquare, folder = 'event', shape = 'circle' } = props;
  const isSquare = shape === 'square';
  const listType = isSquare ? 'picture-card' : 'picture-circle';
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const uploadRef = useRef<any>(null);

  // Crop modal state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFilePreview, setSelectedFilePreview] = useState<string>('');
  const [isCropping, setIsCropping] = useState(false);

  useEffect(() => {
    if (props?.url) {
      setImageUrl(props.url);
    }
  }, [props?.url]);

  const getBase64FromFile = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.addEventListener('load', () => resolve(reader.result as string));
      reader.addEventListener('error', reject);
      reader.readAsDataURL(file);
    });
  };

  const uploadImage = async (file: File, resize?: number): Promise<string> => {
    const formData = new FormData();
    formData.append('image', file);

    const params = new URLSearchParams({ folder });
    if (resize) {
      params.append('resize', resize.toString());
    }

    const response = await fetch(`${imageUploadUrl}?${params.toString()}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to upload image');
    }

    const data = await response.json();
    return data.data as string;
  };

  const cropImage = async (
    imageUrl: string,
    cropArea: CroppedAreaPixels,
    originalWidth: number,
    originalHeight: number,
  ): Promise<string> => {
    const response = await fetch(imageCropUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        image_url: imageUrl,
        crop: {
          x: cropArea.x,
          y: cropArea.y,
          width: cropArea.width,
          height: cropArea.height,
        },
        original_width: originalWidth,
        original_height: originalHeight,
        output_size: 500,
        folder,
      }),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to crop image');
    }

    const data = await response.json();
    return data.data as string;
  };

  const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
        URL.revokeObjectURL(img.src);
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = useCallback(
    async (file: File) => {
      // Validate file
      const isMedia = isAcceptedMediaMime(file.type);
      if (!isMedia) {
        void message.error(mediaAllowedTypesMessage());
        return false;
      }

      const isSizeOk = validateMediaSizeMB(file.size, file.type);
      if (!isSizeOk) {
        const max = isVideoMime(file.type) ? DEFAULT_VIDEO_MAX_MB : DEFAULT_IMAGE_MAX_MB;
        void message.error(`File must be smaller than ${max}MB.`);
        return false;
      }

      // For images, show crop modal
      if (isImageMime(file.type)) {
        const preview = await getBase64FromFile(file);
        setSelectedFile(file);
        setSelectedFilePreview(preview);
        setCropModalOpen(true);
        return false; // Prevent automatic upload
      }

      // For videos, upload directly (no cropping)
      setLoading(true);
      try {
        const url = await uploadImage(file, 1000);
        onChangeFormState('avatarUrl', url);
        setImageUrl(url);
      } catch {
        void message.error('Failed to upload file');
      } finally {
        setLoading(false);
      }

      return false; // We handle upload manually
    },
    [onChangeFormState],
  );

  const handleCropConfirm = async (cropArea: CroppedAreaPixels) => {
    if (!selectedFile) return;

    setIsCropping(true);
    try {
      // Get original image dimensions before upload (for coordinate scaling)
      const { width: originalWidth, height: originalHeight } =
        await getImageDimensions(selectedFile);

      // 1. Upload image with resize
      const originalUrl = await uploadImage(selectedFile, 1000);

      // 2. Crop image on backend (pass original dimensions so backend can scale coordinates)
      const squareUrl = await cropImage(originalUrl, cropArea, originalWidth, originalHeight);

      // 3. Update form state with both URLs
      onChangeFormState('avatarUrl', originalUrl);
      onChangeLogoSquare?.(squareUrl);
      setImageUrl(squareUrl); // Show cropped version in preview

      setCropModalOpen(false);
      setSelectedFile(null);
      setSelectedFilePreview('');

      void message.success('Logo uploaded successfully');
    } catch {
      void message.error('Failed to process logo');
    } finally {
      setIsCropping(false);
    }
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setSelectedFile(null);
    setSelectedFilePreview('');
  };

  const uploadButton = useMemo(
    () => (
      <button style={{ border: 0, background: 'none', cursor: 'pointer' }} type="button">
        {loading ? <LoadingOutlined /> : <PlusOutlined />}
        <div style={{ marginTop: 8 }}>Upload</div>
      </button>
    ),
    [loading],
  );

  const onRemove = () => {
    if (uploadRef.current) {
      const inputElement = uploadRef.current?.upload?.uploader?.fileInput;
      if (inputElement) {
        setFileList([]);
        inputElement.click();
      }
    }
  };

  useImperativeHandle(ref, () => ({ open: onRemove }));

  return (
    <div
      className={`${styles.AvatarUpload}${isSquare ? ` ${styles.AvatarUploadSquare}` : ''} avatar-upload`}
    >
      <Upload
        accept={MEDIA_ACCEPT_EXT}
        name="image"
        listType={listType}
        fileList={fileList}
        beforeUpload={handleFileSelect}
        maxCount={1}
        onRemove={onRemove}
        className={fileList?.length ? 'avatar-upload-visible' : `${styles.AvatarUploadHidden} `}
      />
      <Upload
        ref={uploadRef}
        accept={MEDIA_ACCEPT_EXT}
        name="image"
        listType={listType}
        showUploadList={false}
        beforeUpload={handleFileSelect}
        maxCount={1}
        className={fileList?.length ? `${styles.AvatarUploadHidden}` : 'avatar-upload-visible'}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="avatar"
            style={{
              width: '100px',
              height: '100px',
              borderRadius: isSquare ? 'var(--radius-control)' : '50%',
              objectFit: 'contain',
              backgroundColor: 'var(--surface)',
            }}
          />
        ) : (
          uploadButton
        )}
      </Upload>

      <LogoCropModal
        open={cropModalOpen}
        imageSrc={selectedFilePreview}
        onCancel={handleCropCancel}
        onConfirm={handleCropConfirm}
        isLoading={isCropping}
        shape={shape}
      />
    </div>
  );
});

AvatarUpload.displayName = 'AvatarUpload';

export default AvatarUpload;
